import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react'
import './navbar.component.scss'
import {
  Menu,
  Modal,
  Space,
  Tooltip,
  theme,
  Switch,
  Select,
  InputNumber,
  Popover,
  Alert
} from 'antd'
import { Header } from 'antd/es/layout/layout'
import { useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import {
  CheckCircleFilled,
  InfoCircleOutlined,
  LogoutOutlined,
  PoweroffOutlined,
  PrinterOutlined,
  ReloadOutlined,
  FireFilled,
  WarningFilled,
  QuestionCircleOutlined,
  WifiOutlined,
  SyncOutlined,
  ExclamationCircleFilled
} from '@ant-design/icons'
import { socket } from '../../socket'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import { RootState } from '../../redux/redux_store'
import { SetRestaurantStatus } from '../../redux/userredux/restaurant_slice'
import { axiosInstance } from '../../interceptor/axios_interceptor'
import { LogoutUser, SetVendorInfo } from '../../redux/userredux/user_slice'
import { setLiveMenuCard } from '../../redux/userredux/menu_slice'
import { stopTokenRefreshTimer } from '../../Utility/AuthUtility/tokenRefresh.util'
import { usePrintAgent } from '../../Context/PrintAgentContext'

const NavBar = () => {
  const navigate = useNavigate()
  const page = useParams()
  const dispatch = useDispatch()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reduxUser = useSelector((state: RootState) => state.user)
  const vendorInfo = useSelector(
    (state: RootState) => state.user.vendorInfo
  ) as any
  const restaurantLive = useSelector(
    (state: RootState) => state.restaurant.live
  ) as 'LIVE' | 'SUSPENDED' | 'INACTIVE'
  const [defSelectedKeys, setDefSelectedKeys] = useState(['2'])
  const [live, setLive] = useState<'LIVE' | 'SUSPENDED' | 'INACTIVE'>(
    'INACTIVE'
  )
  const [orderCount, setOrderCount] = useState(0)
  const [vendorInfoLoaded, setVendorInfoLoaded] = useState(false)
  const {
    isConnected: isPrintAgentConnected,
    reconnect: reconnectPrintAgent,
    connectionTarget
  } = usePrintAgent()
  const [retryCount, setRetryCount] = useState(0)
  const [retryTimeout, setRetryTimeout] = useState<NodeJS.Timeout | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)

  // Rush Hour State
  const [isRushHour, setIsRushHour] = useState(false)
  const [isRushHourModalVisible, setIsRushHourModalVisible] = useState(false)
  const [rushHourDurationType, setRushHourDurationType] = useState<number>(60) // in minutes
  const [customRushHourDuration, setCustomRushHourDuration] = useState<
    number | null
  >(null)
  const [isSocketConnected, setIsSocketConnected] = useState(socket.connected)
  const liveRef = useRef(live)
  const items = [
    {
      key: '2',
      label: 'Dashboard',
      route: 'dashboard'
    },
    {
      key: '1',
      label: 'Menu',
      route: 'menu'
    },
    {
      key: '3',
      label: 'Profile',
      route: 'profile'
    },
    // {
    //   key: '4',
    //   label: 'Analytics',
    //   route: 'analytics'
    // },
    // {
    //   key: '5',
    //   label: 'Reports',
    //   route: 'reports'
    // },
    {
      key: '6',
      label: 'Promotions',
      route: 'promotions'
    }
  ]
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken()

  const selctionChange = (key: string | undefined) => {
    console.log(key)
    const route = items.find((item) => item.key === key)?.route
    route ? navigate(route) : ''
  }

  const { confirm } = Modal;

  const signOut = async () => {
    console.log('restaurantLive', restaurantLive)
    if (restaurantLive !== 'INACTIVE') {
      alert(
        restaurantLive === 'LIVE'
          ? 'Cannot Signout when Restaurant is Live'
          : 'Cannot Signout when Restaurant has active orders'
      )
      return ''
    }

    confirm({
      title: 'Confirm Logout',
      icon: <ExclamationCircleFilled />,
      content: 'Are you sure you want to log out?',
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk() {
        // Stop proactive refresh timer before logout
        console.log('[LOGOUT] Stopping token refresh timer')
        stopTokenRefreshTimer()

        // Dispatch LogoutUser — the saga handles the API call to /api/auth/vendorlogout
        dispatch(LogoutUser({}))
        navigate('/vendor/login')
      },
      onCancel() {
        console.log('Logout cancelled')
      }
    })
  }

  useLayoutEffect(() => {
    setDefSelectedKeys(() => {
      if (page['*'] === 'menu') {
        return ['1']
      } else if (page['*'] === 'dashboard') {
        return ['2']
      } else if (page['*'] === 'profile') {
        return ['3']
      } else if (page['*'] === 'analytics') {
        return ['4']
      } else if (page['*'] === 'reports') {
        return ['5']
      } else if (page['*']?.startsWith('promotions')) {
        return ['6']
      }
      return []
    })
  }, [page])

  useEffect(() => {
    setLive(restaurantLive)
  }, [restaurantLive])

  // Keep liveRef in sync so event listeners always see the latest value
  useEffect(() => {
    liveRef.current = live
  }, [live])

  // Heartbeat loop: emit every 20s when LIVE
  useEffect(() => {
    let intervalId: NodeJS.Timeout
    if (live === 'LIVE' && socket.connected && sessionId) {
      intervalId = setInterval(() => {
        socket.emit('heartbeat', {
          restaurantId: reduxUser.restaurantId,
          sessionId
        })
      }, 20000)
    }
    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [live, sessionId, socket.connected, reduxUser.restaurantId])

  // Retry connection function
  const retryConnection = () => {
    // Infinite retry attempts
    const delay = Math.min(1000 * Math.pow(2, retryCount), 30000) // Exponential backoff, max 30 seconds
    console.log(
      `Retrying connection in ${delay / 1000} seconds... (attempt ${retryCount + 1})`
    )

    const timeout = setTimeout(() => {
      setRetryCount((prev) => prev + 1)
      if (reduxUser?.restaurantId) {
        socket.connect()
      }
    }, delay)

    setRetryTimeout(timeout)
  }

  // Clear retry timeout on cleanup
  const clearRetryTimeoutFunc = () => {
    if (retryTimeout) {
      clearTimeout(retryTimeout)
      setRetryTimeout(null)
    }
  }

  const fetchVendorInfo = useCallback(async () => {
    console.log('trying to fetch vendor info')
    try {
      const vendorInfo = await axiosInstance.post(
        '/api/vendor/getvendordetails',
        {
          mobileNumber: reduxUser.mobileNumber,
          type: 'vendorRestaurantDetails'
        }
      )
      dispatch(SetVendorInfo({ vendorInfo: vendorInfo.data.vendorInfo }))
      console.log('vendorInfo', vendorInfo.data.vendorInfo.live)
      setLive(vendorInfo.data.vendorInfo.live)
      setVendorInfoLoaded(true)
      console.log(vendorInfo)

      // Fetch live menu and store in Redux for cuisine-based KOT routing
      const restaurantId = vendorInfo.data.vendorInfo.restaurantId
      if (restaurantId) {
        try {
          const menuRes = await axiosInstance.get(
            `/api/vendor/menu/live/${restaurantId}`
          )
          if (
            menuRes.data.responseCode === 0 &&
            menuRes.data.data?.menu?.menuCard
          ) {
            dispatch(setLiveMenuCard(menuRes.data.data.menu.menuCard))
          }
        } catch (menuErr) {
          console.log('Error fetching live menu for Redux:', menuErr)
        }
      }
    } catch (error) {
      alert(error)
    }
  }, [reduxUser.mobileNumber, dispatch])

  const fetchVendorInfoRef = useRef(fetchVendorInfo)
  useEffect(() => {
    fetchVendorInfoRef.current = fetchVendorInfo
  }, [fetchVendorInfo])

  useEffect(() => {
    fetchVendorInfo()
  }, [fetchVendorInfo])

  // Initial check on navbar load:
  // Runs only after fetchVendorInfo has completed (vendorInfoLoaded = true)
  useEffect(() => {
    if (!vendorInfoLoaded || !reduxUser.restaurantId) return
    const storedStatus = vendorInfo?.live

    // If the restaurant was SUSPENDED, we connect socket but immediately emit goOffline
    // so order updates flow but no new orders can be placed
    if (storedStatus === 'SUSPENDED') {
      if (!socket.connected) {
        socket.io.opts.query = {
          restaurantId: reduxUser.restaurantId,
          live: 'SUSPENDED'
        }
        socket.connect()
      }
      setTimeout(() => {
        socket.emit('goOffline', { restaurantId: reduxUser.restaurantId })
      }, 1000)
    } else {
      setLive(storedStatus || 'INACTIVE')
    }

    // Set Rush Hour State
    if (vendorInfo?.isrushHours) {
      if (
        !vendorInfo.rushHourExpiryTime ||
        new Date(vendorInfo.rushHourExpiryTime) > new Date()
      ) {
        setIsRushHour(true)
      } else {
        setIsRushHour(false)
      }
    } else {
      setIsRushHour(false)
    }
  }, [
    reduxUser.restaurantId,
    vendorInfoLoaded,
    vendorInfo?.live,
    vendorInfo?.isrushHours,
    vendorInfo?.rushHourExpiryTime
  ])

  useEffect(() => {
    console.log('live status changed:', live)

    // Set up event handlers FIRST (before any connect/disconnect actions)
    const handleConnect = () => {
      console.log('Socket connected successfully')
      setRetryCount(0) // Reset retry count on successful connection
      clearRetryTimeoutFunc() // Clear any pending retry
      setIsSocketConnected(true)
      dispatch(SetRestaurantStatus(live))
      fetchVendorInfoRef.current() // Query backend to update current live state
      navigate('dashboard')
    }

    const handleConnectError = (error: Error) => {
      console.error('Connection Error:', error)
      // Attempt retry on connection error
      retryConnection()
    }

    const handleDisconnect = (reason: string) => {
      console.log('Socket disconnected:', reason)
      setIsSocketConnected(false)
      // Only retry if disconnect was not intentional
      if (
        reason !== 'io client disconnect' &&
        (live === 'LIVE' || live === 'SUSPENDED')
      ) {
        console.log('Unexpected disconnect, attempting to reconnect...')
        retryConnection()
      } else if (live === 'INACTIVE') {
        console.log('Disconnect confirmed for INACTIVE status')
        dispatch(SetRestaurantStatus('INACTIVE'))
      }
    }

    const handleConnected = (data: { status: string; sessionId: string }) => {
      if (data.sessionId) setSessionId(data.sessionId)
    }

    const handleSuspendedAck = () => {
      setLive('SUSPENDED')
      dispatch(SetRestaurantStatus('SUSPENDED'))
    }

    const handleOfflineAck = () => {
      setLive('INACTIVE')
      dispatch(SetRestaurantStatus('INACTIVE'))
      if (socket.connected) socket.disconnect()
    }

    const handleLiveAck = () => {
      setLive('LIVE')
      dispatch(SetRestaurantStatus('LIVE'))
      if (!socket.connected) socket.connect()
    }

    const handleRushHourEvent = (data: { isrushHours: boolean }) => {
      console.log('Received rushHour event:', data)
      setIsRushHour(data.isrushHours)
    }

    const handleOnline = () => {
      console.log('Internet connection restored, forcing socket reconnect...')
      if ((live === 'LIVE' || live === 'SUSPENDED') && !socket.connected) {
        clearRetryTimeoutFunc()
        setRetryCount(0)
        socket.connect()
      }
    }

    // Register event listeners
    window.addEventListener('online', handleOnline)
    socket.on('connect', handleConnect)
    socket.on('connected', handleConnected)
    socket.on('connect_error', handleConnectError)
    socket.on('disconnect', handleDisconnect)
    socket.on('suspended_ack', handleSuspendedAck)
    socket.on('offline_ack', handleOfflineAck)
    socket.on('live_ack', handleLiveAck)
    socket.on('rushHour', handleRushHourEvent)

    if (live === 'LIVE' || live === 'SUSPENDED') {
      console.log('liveorSuspended', live)
      console.log('socket connected status:', socket.connected)

      // Set query parameters for socket connection
      socket.io.opts.query = {
        restaurantId: reduxUser?.restaurantId,
        live: live
      }

      // Only connect if not already connected
      if (!socket.connected) {
        console.log('Initiating socket connection...')
        socket.connect()
      } else {
        console.log('Socket already connected, skipping connection attempt')
        // Already connected, just update the status
        dispatch(SetRestaurantStatus(live))
      }
    } else {
      console.log('Status is INACTIVE, disconnecting socket')
      clearRetryTimeoutFunc() // Clear any pending retries when going offline
      setRetryCount(0) // Reset retry count

      if (socket.connected) {
        console.log('Calling socket.disconnect()...')
        socket.disconnect()
      } else {
        console.log('Socket already disconnected')
        dispatch(SetRestaurantStatus('INACTIVE'))
      }
    }

    return () => {
      clearRetryTimeoutFunc() // Clean up timeout on unmount
      // Remove event listeners to prevent memory leaks
      window.removeEventListener('online', handleOnline)
      socket.off('connect', handleConnect)
      socket.off('connected', handleConnected)
      socket.off('connect_error', handleConnectError)
      socket.off('disconnect', handleDisconnect)
      socket.off('suspended_ack', handleSuspendedAck)
      socket.off('offline_ack', handleOfflineAck)
      socket.off('live_ack', handleLiveAck)
      socket.off('rushHour', handleRushHourEvent)
    }
  }, [live])

  const liveChange = async () => {
    try {
      if (live === 'INACTIVE') {
        setLive('LIVE')
        console.log('Status set to attempt LIVE')
        return
      }

      if (live === 'SUSPENDED') {
        // Toggle from SUSPENDED usually means going fully LIVE again
        if (socket.connected) socket.disconnect()
        setLive('LIVE')
        return
      }

      if (live === 'LIVE') {
        const response = await axiosInstance.get(
          `/api/vendor/getActiveOrders/${reduxUser.restaurantId}`
        )
        if (response.data.count > 0) {
          alert(
            'Cannot go offline while there are active orders. New orders will not be placed.'
          )
        }
        socket.emit('goOffline', { restaurantId: reduxUser.restaurantId })
      }
    } catch (error) {
      console.error('Error changing live status:', error)
      alert('Failed to update restaurant status. Please try again.')
    }
  }

  const handleRushHourToggle = (checked: boolean) => {
    if (checked) {
      setIsRushHourModalVisible(true)
    } else {
      handleSetRushHour(false)
    }
  }

  const handleSetRushHour = async (
    checked: boolean,
    durationMinutes?: number
  ) => {
    try {
      await axiosInstance.post('/api/vendor/setDynamicrushHours', {
        restaurantId: reduxUser.restaurantId,
        isrushHours: checked,
        durationMinutes: checked ? durationMinutes : undefined
      })
      setIsRushHour(checked)
      setIsRushHourModalVisible(false)
      // refresh vendor details to update redux store if needed, or rely on local state
    } catch (error) {
      console.error('Failed to update rush hour:', error)
      alert('Failed to update rush hour status')
      setIsRushHourModalVisible(false)
    }
  }

  return (
    <>
      <Header className='app-navbar'>
        <div className='demo-logo'>
          <img
            src='https://foodtolls-prod-uploads.s3.ap-south-1.amazonaws.com/Logo/LogoWhite.png'
            alt='whitelogo'
          />
        </div>
        <Menu
          className='nav-menu'
          theme='dark'
          mode='horizontal'
          defaultSelectedKeys={['2']}
          selectedKeys={defSelectedKeys}
          items={items}
          style={{
            flex: 1,
            minWidth: 0
          }}
          onSelect={({ key }) => selctionChange(key)}
        />
        <Space className='right-actions' size='middle' style={{ display: 'flex', alignItems: 'center' }}>
          {vendorInfo?.restaurantName && (
            <div className='restaurant-name-tag'>
              {vendorInfo.restaurantName}
            </div>
          )}

          <Tooltip
            title={
              isPrintAgentConnected
                ? `Print Agent connected (${connectionTarget})`
                : `Print Agent offline. Click to retry (${connectionTarget})`
            }
          >
            <button
              type='button'
              className={
                isPrintAgentConnected
                  ? 'status-chip status-chip--online'
                  : 'status-chip status-chip--offline'
              }
              onClick={() => {
                if (!isPrintAgentConnected) {
                  reconnectPrintAgent()
                }
              }}
            >
              <PrinterOutlined className='status-chip__icon' />
              <span className='status-chip__label'>Printer</span>
              <span className='status-chip__value'>
                {isPrintAgentConnected ? 'Online' : 'Offline'}
              </span>
              {isPrintAgentConnected ? (
                <CheckCircleFilled className='status-chip__state-icon' />
              ) : (
                <ReloadOutlined className='status-chip__state-icon' />
              )}
            </button>
          </Tooltip>

          {/* Rush Hour Toggle */}
          <div className='rush-chip'>
            <span className='rush-chip__label'>
              <FireFilled />
              <span className='rush-text'>Rush</span>
            </span>
            <Switch
              checked={isRushHour}
              onChange={handleRushHourToggle}
              checkedChildren='ON'
              unCheckedChildren='OFF'
              style={{ backgroundColor: isRushHour ? '#ff4d4f' : undefined }}
              disabled={live !== 'LIVE'}
            />
            {live !== 'LIVE' && (
              <Tooltip title='Enable store live mode to toggle rush hour'>
                <WarningFilled className='rush-chip__warn' />
              </Tooltip>
            )}
          </div>

          {live === 'INACTIVE' ? (
            <div onClick={() => liveChange()} className={'power_off'}>
              <PoweroffOutlined />
            </div>
          ) : (
            <div
              className="live-indicator-wrapper"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Tooltip title={isSocketConnected ? 'Connected to live orders' : 'Connection lost, trying to reconnect...'}>
                <div style={{ color: isSocketConnected ? '#52c41a' : '#ff4d4f', fontSize: '18px', display: 'flex', alignItems: 'center' }}>
                  {isSocketConnected ? <WifiOutlined /> : <SyncOutlined spin />}
                </div>
              </Tooltip>
              <div
                onClick={() => liveChange()}
                className={live === 'LIVE' ? 'power_on' : 'power_suspended'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <PoweroffOutlined />
              </div>
            </div>
          )}
        </Space>

        <Space className='logout-space' style={{ marginLeft: '30px', display: 'flex', alignItems: 'center' }}>
          <Popover
            content={
              <div>
                <div><span style={{ fontWeight: 600 }}>Support:</span> +91 9019637553</div>
                {/* <div><span style={{fontWeight: 600}}>Alternate:</span> +91 8888888888</div> */}
              </div>
            }
            title="Help & Support"
            trigger="hover"
            placement="bottomRight"
          >
            <div style={{ color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <QuestionCircleOutlined style={{ fontSize: '20px' }} />
              <span className="help-text">Help</span>
            </div>
          </Popover>
          <LogoutOutlined
            onClick={() => signOut()}
            className="logout-btn"
            style={{
              background: 'white',
              borderRadius: '50%',
              marginLeft: '10px'
            }}
          />
        </Space>
      </Header>

      <div className="mobile-nav-container">
        <Menu
          className='mobile-nav-menu'
          theme='dark'
          mode='horizontal'
          defaultSelectedKeys={['2']}
          selectedKeys={defSelectedKeys}
          items={items}
          onSelect={({ key }) => selctionChange(key)}
        />
      </div>

      {/* Rush Hour Duration Modal */}
      <Modal
        title='Set Rush Hour Duration'
        open={isRushHourModalVisible}
        onOk={() => {
          const duration =
            rushHourDurationType === -1
              ? customRushHourDuration
              : rushHourDurationType
          if (!duration || duration <= 0) {
            alert('Please enter a valid duration.')
            return
          }
          handleSetRushHour(true, duration)
        }}
        onCancel={() => setIsRushHourModalVisible(false)}
        okText='Start Rush Hour'
      >
        <p style={{ marginBottom: 16 }}>
          Select how long the rush hour should last:
        </p>
        <Select
          value={rushHourDurationType}
          style={{ width: '100%', marginBottom: 16 }}
          onChange={(value) => setRushHourDurationType(value)}
          options={[
            { value: 60, label: '1 Hour' },
            { value: 120, label: '2 Hours' },
            { value: 240, label: '4 Hours' },
            { value: 1440, label: 'All Day (24 Hours)' },
            { value: -1, label: 'Custom' }
          ]}
        />
        {rushHourDurationType === -1 && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: 8 }}>Duration (minutes):</span>
            <InputNumber
              min={1}
              value={customRushHourDuration}
              onChange={(value) => setCustomRushHourDuration(value as number)}
              style={{ width: '100%' }}
            />
          </div>
        )}
      </Modal>
    </>
  )
}

export default NavBar
